import React, {ComponentType, useMemo, useRef, useState} from 'react';
import Variable from '../../lib/metadata';
import './table.scss';
import {dataRow, dataRows} from '../../lib/data-types';
import HeaderMenu from './header-menu';
import Cell from './cell';
import HeaderName from './header-name';
import SortIcon from './sort-icon';

export const MAX_STICKY_HEADERS = 5;
const STICKY_HEADER_WIDTH = 230;

export default function Table(props: {
    headers: Array<Variable>,
    data: dataRows,
    children?: ComponentType<{row: dataRow}>,
    onFilter?: (variable: Variable) => void,
    onInfo?: (variable: Variable) => void,
    onSort?: (variable: Variable) => void,
    onRemove?: (variable: Variable) => void,
    sorts?: Array<[Variable, boolean]>,
    getColorIndex?: (variable: Variable) => number,
    onRowClick?: (row: dataRow) => void,
    hiddenColumns?: Array<Variable>,
    stickyColumns?: Array<Variable>,
    tableBottomMessage?: string
}) {
    let {data, headers, sorts, onSort, onFilter, onInfo, onRemove, getColorIndex, onRowClick, hiddenColumns, stickyColumns, tableBottomMessage} = props;
    const [showFilter, setShowFilter] = useState<boolean>(false);
    const tableRef = useRef<HTMLDivElement | null>(null);
    const enableSticky: boolean = useMemo(() => (!showFilter && !!stickyColumns && stickyColumns.length > 0), [showFilter, stickyColumns]);

    // Sorting
    if (!sorts)
        sorts = [];

    const filteredHeaders = useMemo(() => {
        return headers.filter(h => !hiddenColumns || !hiddenColumns.some(hidden => h.getId() === hidden.getId()));
    }, [headers, hiddenColumns]);

    const stickyHeaders = useMemo(() => {
        let stickyHeaders = filteredHeaders.filter(v => isStickyVariable(v));
        if (stickyHeaders.length > MAX_STICKY_HEADERS)
            stickyHeaders = stickyHeaders.slice(0, MAX_STICKY_HEADERS);
        return stickyHeaders;
    }, [filteredHeaders]);

    const displayHeaders = useMemo(() => {
        return [
            ...stickyHeaders,
            ...filteredHeaders.filter(v => !stickyHeaders.some(sv => sv.name === v.name))
        ];
    }, [stickyHeaders, filteredHeaders, ]);

    const colorIndices = useMemo(() => {
        return displayHeaders.map(h => getColorIndex ? getColorIndex(h) : -1);
    }, [displayHeaders, getColorIndex]);

    const onStickyMouseOver = (i: number, header: Variable) => {
        if (i < MAX_STICKY_HEADERS && isStickyVariable(header)) {
            if (tableRef && tableRef.current)
                tableRef.current.scrollLeft = 0;
            setShowFilter(true);
        }
    };

    function isStickyVariable(v: Variable) {
        return stickyColumns?.includes(v);
    }


    return <div className="insight-table" ref={tableRef}>
        <table>
            <thead>
                <tr className={stickyHeaders.length === 0 ? 'border-b border-gray-300' : ''}>
                    {displayHeaders.map((h, i) => <th data-testid="sort-button"
                        key={i}
                        className={`${i < MAX_STICKY_HEADERS && isStickyVariable(h) ? 'sticky-col' : ''} bg-gray-200`}
                        style={{left: `${i < MAX_STICKY_HEADERS && isStickyVariable(h) ? STICKY_HEADER_WIDTH * i + 'px' : 'auto'}`}}
                        onClick={() => onSort && onSort(h)}
                        onMouseOver={() => onStickyMouseOver(i, h)}
                        onMouseLeave={() => setShowFilter(false)}>
                        <HeaderName value={h.label} />
                        <SortIcon variable={h} sorts={sorts!} />
                        <HeaderMenu variable={h} onFilter={onFilter} onInfo={onInfo} onRemove={onRemove} />
                    </th>)}
                </tr>
            </thead>
            <tbody>
                {data.map((row, i) => <tr data-testid="data-row"
                    key={i}
                    className={`${stickyHeaders.length > 0 ? 'sticky-col' : ''} ${i % 2 === 0 ? 'dark' : ''}`}
                    onClick={() => onRowClick && onRowClick(row)}>
                    {displayHeaders.map((h, i) => <Cell
                        key={i}
                        variable={h}
                        row={row}
                        colorIndex={colorIndices[i]}
                        left={`${i < MAX_STICKY_HEADERS && isStickyVariable(h) ? STICKY_HEADER_WIDTH * i + 'px' : 'auto'}`}
                        sticky={i < MAX_STICKY_HEADERS && isStickyVariable(h)}
                        enableSticky={enableSticky}
                        lastSticky={i === stickyHeaders.length - 1}
                        allowPopup={typeof onRowClick !== 'function'}
                    />)}
                </tr>)}
            </tbody>
        </table>
        {tableBottomMessage && <div className="w-full mt-5 p-3 bg-white text-center">{tableBottomMessage}</div>}
    </div>;
}
